import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import RegionalService from '../services/RegionalService';

// Crear el contexto de autenticación
const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Funciones para manejar cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  const result = parts.length === 2 ? parts.pop().split(';').shift() : null;
  
  if (result) {
  }
  
  return result;
};

const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  const cookieString = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  document.cookie = cookieString;
  
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

const getDecodedCookie = (name) => {
  const value = getCookie(name);
  if (!value) return null;
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const applyRoleMetadata = (userData = {}) => {
  const normalizedUser = { ...userData };
  const cookieRoleId = getDecodedCookie('user_role_id');
  const cookieRoleName = getDecodedCookie('user_role_name');

  if (!normalizedUser.roleId && cookieRoleId) {
    normalizedUser.roleId = cookieRoleId;
  }
  if (!normalizedUser.roleName && cookieRoleName) {
    normalizedUser.roleName = cookieRoleName;
  }
  if (!normalizedUser.role && normalizedUser.roleName) {
    normalizedUser.role = normalizedUser.roleName;
  }

  return normalizedUser;
};

// Provider del contexto de autenticación
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const persistUserSession = (nextUserData = {}) => {
    const normalizedUser = applyRoleMetadata(nextUserData);
    const token = normalizedUser?.token || null;

    if (token) {
      setCookie('auth_token', token, 7);
      apiService.setAuthToken(token);
    }
    if (normalizedUser.refreshToken) {
      apiService.setRefreshToken(normalizedUser.refreshToken);
    }

    setCookie('user_data', encodeURIComponent(JSON.stringify(normalizedUser)), 7);
    if (normalizedUser.roleId) {
      setCookie('user_role_id', encodeURIComponent(normalizedUser.roleId), 7);
    }
    if (normalizedUser.roleName) {
      setCookie('user_role_name', encodeURIComponent(normalizedUser.roleName), 7);
    }

    setUser(normalizedUser);
    setIsAuthenticated(true);
    return normalizedUser;
  };

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        
        const authToken = getCookie('auth_token');
        const userData = getCookie('user_data');
        
        if (authToken && userData) {
          try {
            const parsedUser = JSON.parse(decodeURIComponent(userData));
            const normalizedUser = applyRoleMetadata(parsedUser);
            setUser(normalizedUser);
            setIsAuthenticated(true);

            // Configurar tokens en ApiService al restaurar la sesión
            apiService.setAuthToken(authToken);
            // getRefreshToken() ya lee de localStorage como fallback, no hace falta setar explícitamente

          } catch (error) {
            console.error('❌ Error al parsear datos del usuario:', error);
            logout();
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('❌ Error al verificar autenticación:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    // Función movida dentro del useEffect para evitar dependencias
    // Se mantiene aquí para poder ser llamada externamente si es necesario
    const authToken = getCookie('auth_token');
    const userData = getCookie('user_data');
    
    if (authToken && userData) {
      try {
        const parsedUser = JSON.parse(decodeURIComponent(userData));
        const normalizedUser = applyRoleMetadata(parsedUser);
        setUser(normalizedUser);
        setIsAuthenticated(true);
        
        // También configurar el token en ApiService al recargar
        apiService.setAuthToken(authToken);
        
      } catch (error) {
        console.error('❌ Error al recargar autenticación:', error);
        logout();
      }
    } else {
      logout();
    }
  };

  const login = async (userData) => {
    try {
      setIsLoading(true);

      // Los datos ya vienen validados del UsuarioService, solo guardamos
      const roleId = userData.idRol || userData.roleId || null;
      const roleName = userData.nombreRol || userData.roleName || userData.role || 'Usuario';

      const areaName = userData.area || userData.Area || userData.areaNombre || userData.nombreArea || '';

      const userDataToStore = {
        id: userData.email, // Usar email como ID único
        email: userData.email,
        name: userData.name,
        displayName: userData.name,
        pictureUrl: userData.pictureUrl || '',
        token: userData.token,
        pertmiteCrearAsistente: userData.pertmiteCrearAsistente || false,
        roleId,
        roleName,
        role: roleName,
        lastLogin: new Date().toISOString(),
        area: areaName,
        statusCode: userData.statusCode,
        availableAccessContexts: userData.availableAccessContexts || [],
        currentAccessContext: userData.currentAccessContext || null,
        requiresAccessContextSelection: Boolean(userData.requiresAccessContextSelection),
        requirePasswordChange: Boolean(userData.requirePasswordChange)
      };

      // Persistir refresh token en ApiService y localStorage
      if (userData.refreshToken) {
        apiService.setRefreshToken(userData.refreshToken);
      }

      const persistedUser = persistUserSession(userDataToStore);
      return { success: true, user: persistedUser };
    } catch (error) {
      console.error('❌ Error procesando login:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    const refreshToken = apiService.getRefreshToken();

    // Limpiar estado local inmediatamente
    deleteCookie('auth_token');
    deleteCookie('user_data');
    deleteCookie('user_role_id');
    deleteCookie('user_role_name');
    RegionalService.clearRegionalData();
    apiService.setAuthToken(null);
    apiService.setRefreshToken(null);
    setUser(null);
    setIsAuthenticated(false);

    // Invalidar refresh token en el servidor (fire-and-forget)
    if (refreshToken) {
      fetch(`${apiService.baseURL}/User/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      }).catch(() => {});
    }
  };

  const updateUser = (newUserData) => {
    const updatedUser = { ...(user || {}), ...newUserData };
    persistUserSession(updatedUser);
  };

  const updateToken = (token) => {
    if (!token) return;
    persistUserSession({ ...(user || {}), token });
  };

  const applyAccessContext = (sessionPatch = {}) => {
    const mergedUser = { ...(user || {}), ...sessionPatch };
    return persistUserSession(mergedUser);
  };

  // Función para manejar errores 401 y logout automático
  const handleUnauthorized = () => {
    console.warn('🔐 Error 401 detectado - Cerrando sesión automáticamente');
    
    // Ejecutar logout inmediatamente
    logout();
    
    // No forzar redirección aquí, dejar que ProtectedRoute maneje la lógica
  };

  // Funciones auxiliares eliminadas - ya no son necesarias

  const contextValue = {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    updateUser,
    updateToken,
    applyAccessContext,
    checkAuthStatus,
    handleUnauthorized
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
